import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LayoutDashboard, User, Package, Briefcase,
  ShoppingCart, FileText, LogOut, Menu, X,
  ChevronRight, Building2, Bell
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import { fetchProfile } from '../api/supplierApi';
import DashboardSection from './supplier/DashboardSection';
import ProfileSection from './supplier/ProfileSection';
import ProductsSection from './supplier/ProductsSection';
import ServicesSection from './supplier/ServicesSection';
import OrdersSection from './supplier/OrdersSection';
import DocumentsSection from './supplier/DocumentsSection';
import NotificationBell from '../components/NotificationBell';

const NAV_ITEMS = [
  { key: 'dashboard',  labelTr: 'Genel Bakış',  labelEn: 'Dashboard',  icon: LayoutDashboard },
  { key: 'profile',    labelTr: 'Profil',        labelEn: 'Profile',    icon: User },
  { key: 'products',   labelTr: 'Ürünler',       labelEn: 'Products',   icon: Package },
  { key: 'services',   labelTr: 'Hizmetler',     labelEn: 'Services',   icon: Briefcase },
  { key: 'orders',     labelTr: 'Siparişler',    labelEn: 'Orders',     icon: ShoppingCart },
  { key: 'documents',  labelTr: 'Belgeler',      labelEn: 'Documents',  icon: FileText },
];

export default function SupplierDashboard() {
  const navigate = useNavigate();
  const { slug, section = 'dashboard' } = useParams();
  const { language } = useLanguage();
  const tr = language === 'tr';

  // URL param'dan aktif bölüm türetiliyor
  const activeSection = NAV_ITEMS.find(n => n.key === section) ? section : 'dashboard';

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({ full_name: '', email: '' });
  const [companyInfo, setCompanyInfo] = useState({ company_name: '', logo_url: null });

  // Bölüm navigasyonu — URL'i günceller
  const goSection = (key) => {
    setSidebarOpen(false);
    const base = slug
      ? `/tedarikci-admin/${slug}/dashboard`
      : '/tedarikci-admin/dashboard';
    navigate(`${base}/${key}`);
  };

  useEffect(() => {
    const token = localStorage.getItem('cop31_supplier_token');
    if (!token) { navigate('/giris?tab=supplier'); return; }
    try {
      const userData = JSON.parse(localStorage.getItem('cop31_supplier_user') || '{}');
      setUserInfo({ full_name: userData.full_name || '', email: userData.email || '' });
    } catch {
      // Parse error — still valid, just no display info
    }
    // Fetch profile for company name + logo
    fetchProfile()
      .then(p => setCompanyInfo({ company_name: p.company_name || '', logo_url: p.logo_url || null }))
      .catch(() => {});
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/giris?tab=supplier');
  };

  const navLabel = (item) => tr ? item.labelTr : item.labelEn;  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':  return <DashboardSection tr={tr} />;
      case 'profile':    return <ProfileSection tr={tr} onProfileUpdate={(p) => setCompanyInfo({ company_name: p.company_name || '', logo_url: p.logo_url || null })} />;
      case 'products':   return <ProductsSection tr={tr} />;
      case 'services':   return <ServicesSection tr={tr} />;
      case 'orders':     return <OrdersSection tr={tr} />;
      case 'documents':  return <DocumentsSection tr={tr} />;
      default:           return <DashboardSection tr={tr} />;
    }
  };

  const activeItem = NAV_ITEMS.find(n => n.key === activeSection);

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* ─── Sidebar Overlay (mobile) ─────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ─── Sidebar ────────────────────────────────────────────────── */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-64 bg-white border-r border-gray-100 shadow-xl
          flex flex-col transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:shadow-none lg:z-auto
        `}
      >
        {/* Logo / Brand */}
        <div className="px-5 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {tr ? 'Tedarikçi Portalı' : 'Supplier Portal'}
              </p>
              <p className="text-xs text-gray-400">COP 31</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto text-gray-400 hover:text-gray-600 lg:hidden"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* User info */}
        <div className="px-4 py-4 border-b border-gray-50">
          <div className="bg-amber-50 rounded-2xl px-3 py-3 space-y-1">
            {/* Company name (bold, top) */}
            <p className="text-xs font-extrabold text-amber-900 truncate">
              {companyInfo.company_name || (tr ? 'Firma Adı Yok' : 'No Company')}
            </p>
            {/* User full name */}
            <p className="text-xs font-semibold text-amber-800 truncate">{userInfo.full_name || '—'}</p>
            {/* Email */}
            <p className="text-[11px] text-amber-600 truncate">{userInfo.email}</p>
            {/* Role badge */}
            <span className="inline-block text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium uppercase tracking-wide">
              {tr ? 'Tedarikçi' : 'Supplier'}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.key;
            return (
              <button
                key={item.key}
                data-testid={`nav-${item.key}`}
                onClick={() => goSection(item.key)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                  transition-all duration-150 text-left
                  ${isActive
                    ? 'bg-amber-500 text-white shadow-sm shadow-amber-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                <span className="flex-1">{navLabel(item)}</span>
                {isActive && <ChevronRight className="h-3.5 w-3.5 text-white/70" />}
              </button>
            );
          })}
        </nav>

        {/* Bottom: Language + Logout */}
        <div className="px-3 py-4 border-t border-gray-100 space-y-2">
          <div className="px-1">
            <LanguageToggle variant="admin" />
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            {tr ? 'Çıkış Yap' : 'Log Out'}
          </button>
        </div>
      </aside>

      {/* ─── Main Content ───────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar (mobile + breadcrumb) */}
        <header className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 sm:px-6 h-14 flex items-center gap-4">
          {/* Hamburger */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
            data-testid="hamburger-btn"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm min-w-0">
            <span className="text-gray-400 hidden sm:inline">{tr ? 'Tedarikçi Portalı' : 'Supplier Portal'}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-300 hidden sm:inline" />
            <span className="font-semibold text-gray-800 truncate">
              {activeItem ? navLabel(activeItem) : ''}
            </span>
          </div>

          {/* Right actions */}
          <div className="ml-auto flex items-center gap-3">
            <div className="rounded-full bg-gray-800 p-1">
              <NotificationBell
                language={language}
                onNavigate={(link) => {
                  if (link && link.includes('/tedarikci-admin/orders/')) {
                    navigate(`/tedarikci-admin/${slug}/orders`);
                  }
                }}
              />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8" data-testid="supplier-main-content">
          {renderSection()}
        </main>
      </div>
    </div>
  );
}
